import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { createEventService } from "@/core/services/factory";
import type {
  EventLibraryCategoryFilter,
  EventLibraryDateFilters,
  EventLibraryTab,
} from "@/core/services/types/event.types";
import { downloadTextFile } from "@/lib/downloadFile";
import { formatRangeSummary } from "@/lib/dateRange";
import {
  buildEventsLibraryCsv,
  buildEventsLibraryExportColumns,
  buildEventsLibraryExportFilename,
  buildEventsLibraryExportRows,
  buildEventsLibraryTxt,
} from "@/lib/eventsLibraryExport";
import { useTranslations } from "@/lib/useTranslations";

interface EventsLibraryExportDialogProps {
  open: boolean;
  onClose: () => void;
  tab: EventLibraryTab;
  category: EventLibraryCategoryFilter;
  dateFilters: EventLibraryDateFilters;
  searchQuery: string;
  total: number;
}

type ExportFormat = "csv" | "txt";

export function EventsLibraryExportDialog({
  open,
  onClose,
  tab,
  category,
  dateFilters,
  searchQuery,
  total,
}: EventsLibraryExportDialogProps) {
  const t = useTranslations("events.library");
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  function buildTxtRecapDetailLines(): string[] {
    const lines: string[] = [];

    const trimmedSearch = searchQuery.trim();
    if (trimmedSearch) {
      lines.push(trimmedSearch);
    }

    if (tab !== "ALL") {
      lines.push(t(`statuses.${tab}`));
    }

    if (category !== "ALL") {
      lines.push(t(`categoryFilters.${category}`));
    }

    if (dateFilters.startFrom || dateFilters.startTo) {
      lines.push(
        `${t("filtersStartDateLabel")} : ${formatRangeSummary(
          dateFilters.startFrom,
          dateFilters.startTo,
          t("filtersDateAllLabel"),
        )}`,
      );
    }

    if (dateFilters.endFrom || dateFilters.endTo) {
      lines.push(
        `${t("filtersEndDateLabel")} : ${formatRangeSummary(
          dateFilters.endFrom,
          dateFilters.endTo,
          t("filtersDateAllLabel"),
        )}`,
      );
    }

    return lines;
  }

  async function handleExport(format: ExportFormat) {
    setIsExporting(true);
    setError(null);

    try {
      const events = createEventService();
      const items = await events.listAllByLibraryTab(
        tab,
        searchQuery,
        category,
        dateFilters,
      );

      if (items.length === 0) {
        setError(t("export.empty"));
        return;
      }

      const columns = buildEventsLibraryExportColumns();
      const rows = buildEventsLibraryExportRows(items);
      const filename = buildEventsLibraryExportFilename(tab, format);

      if (format === "csv") {
        downloadTextFile(
          buildEventsLibraryCsv(columns, rows),
          filename,
          "text/csv;charset=utf-8",
        );
      } else {
        downloadTextFile(
          buildEventsLibraryTxt(items, {
            recapDetailLines: buildTxtRecapDetailLines(),
          }),
          filename,
          "text/plain;charset=utf-8",
        );
      }

      onClose();
    } catch {
      setError(t("export.failed"));
    } finally {
      setIsExporting(false);
    }
  }

  function handleClose() {
    if (isExporting) return;
    setError(null);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("export.dialogTitle")}
      panelClassName="max-w-md"
    >
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          {t("export.description", { count: total })}
        </p>

        <div className="grid gap-2">
          <Button
            type="button"
            disabled={isExporting || total === 0}
            onClick={() => handleExport("csv")}
          >
            {t("export.csv")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={isExporting || total === 0}
            onClick={() => handleExport("txt")}
          >
            {t("export.txt")}
          </Button>
        </div>

        {isExporting ? (
          <p className="text-sm text-neutral-500">{t("export.loading")}</p>
        ) : null}

        {error ? (
          <p className="text-sm text-whart-urgent" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
